import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { API } from '@/config';
import { Activity, Database, AlertTriangle, CheckCircle, XCircle, RefreshCw, Trash2 } from 'lucide-react';
import axios from 'axios';

const ServerHealth = ({ token, i18n }) => {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [clearing, setClearing] = useState(false);
  const isAr = i18n?.language === 'ar';

  const fetchHealth = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/admin/server-health`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHealth(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Connection failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchHealth(); }, []);

  const clearErrors = async () => {
    if (!window.confirm(isAr ? 'هل تريد مسح جميع الأخطاء؟' : 'Clear all error logs?')) return;
    setClearing(true);
    try {
      await axios.delete(`${API}/admin/error-logs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchHealth();
    } catch (err) {
      console.error('Failed to clear errors:', err);
    } finally {
      setClearing(false);
    }
  };

  const StatusIcon = ({ ok }) => ok 
    ? <CheckCircle className="w-5 h-5 text-green-500" /> 
    : <XCircle className="w-5 h-5 text-red-500" />;

  if (loading) return (
    <div className="flex items-center justify-center p-12" data-testid="server-health-loading">
      <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      <span className="ml-3 text-lg">{isAr ? 'جاري فحص السيرفر...' : 'Checking server...'}</span>
    </div>
  );

  if (error) return (
    <div className="bg-red-50 border border-red-200 rounded-xl p-6" data-testid="server-health-error">
      <div className="flex items-center gap-3 mb-4">
        <XCircle className="w-8 h-8 text-red-500" />
        <h2 className="text-xl font-bold text-red-700">{isAr ? 'خطأ في الاتصال بالسيرفر' : 'Server Connection Error'}</h2>
      </div>
      <p className="text-red-600 bg-red-100 p-3 rounded font-mono text-sm">{error}</p>
      <button onClick={fetchHealth} className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2">
        <RefreshCw className="w-4 h-4" /> {isAr ? 'إعادة المحاولة' : 'Retry'}
      </button>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="server-health-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold flex items-center gap-3">
          <Activity className="w-7 h-7 text-blue-600" />
          {isAr ? 'تشخيص السيرفر' : 'Server Diagnostics'}
        </h2>
        <button onClick={fetchHealth} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 text-sm">
          <RefreshCw className="w-4 h-4" /> {isAr ? 'تحديث' : 'Refresh'}
        </button>
      </div>

      {/* Server Status */}
      <div className={`p-5 rounded-xl border-2 ${health?.server === 'ok' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
        <div className="flex items-center gap-3">
          <StatusIcon ok={health?.server === 'ok'} />
          <span className="text-lg font-semibold">
            {isAr ? 'حالة السيرفر:' : 'Server Status:'} {health?.server === 'ok' ? (isAr ? 'يعمل بشكل طبيعي' : 'Running') : (isAr ? 'يوجد مشاكل' : 'Degraded')}
          </span>
        </div>
        <p className="text-sm text-gray-500 mt-1">{isAr ? 'آخر فحص:' : 'Last check:'} {new Date(health?.timestamp).toLocaleString(isAr ? 'ar-SA' : 'en-US')}</p>
      </div>

      {/* Database Status */}
      <div className={`p-5 rounded-xl border-2 ${health?.database?.status === 'connected' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
        <div className="flex items-center gap-3 mb-3">
          <Database className="w-6 h-6 text-blue-600" />
          <span className="text-lg font-semibold">{isAr ? 'قاعدة البيانات' : 'Database'}</span>
          <StatusIcon ok={health?.database?.status === 'connected'} />
        </div>
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div className="bg-white p-2 rounded"><strong>{isAr ? 'الحالة:' : 'Status:'}</strong> {health?.database?.status}</div>
          <div className="bg-white p-2 rounded"><strong>{isAr ? 'الاسم:' : 'Name:'}</strong> {health?.database?.name}</div>
        </div>
      </div>

      {/* Collections */}
      <div className="bg-white border rounded-xl p-5">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Database className="w-5 h-5 text-indigo-600" />
          {isAr ? 'المجموعات (Collections)' : 'Collections'}
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {health?.collections && Object.entries(health.collections).map(([name, count]) => (
            <div key={name} className={`p-3 rounded-lg border ${typeof count === 'number' ? 'bg-gray-50' : 'bg-red-50 border-red-200'}`}>
              <div className="font-mono text-sm text-gray-600">{name}</div>
              <div className={`text-2xl font-bold ${typeof count === 'number' ? 'text-indigo-600' : 'text-red-600'}`}>
                {typeof count === 'number' ? count.toLocaleString() : count}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Errors */}
      <div className="bg-white border rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            {isAr ? 'آخر الأخطاء (24 ساعة)' : 'Recent Errors (24h)'}
          </h3>
          {health?.recent_errors?.length > 0 && (
            <button
              onClick={clearErrors}
              disabled={clearing}
              data-testid="clear-errors-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg transition-colors disabled:opacity-50"
            >
              <Trash2 className="w-3.5 h-3.5" />
              {clearing ? (isAr ? 'جارٍ المسح...' : 'Clearing...') : (isAr ? 'مسح الأخطاء' : 'Clear All')}
            </button>
          )}
        </div>
        {health?.recent_errors?.length === 0 ? (
          <div className="text-center py-6 text-green-600 flex items-center justify-center gap-2">
            <CheckCircle className="w-5 h-5" />
            {isAr ? 'لا توجد أخطاء' : 'No errors'}
          </div>
        ) : (
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {health?.recent_errors?.map((err, i) => (
              <div key={i} className="p-3 bg-red-50 border border-red-100 rounded-lg text-sm">
                <div className="flex justify-between">
                  <span className="font-semibold text-red-700">{err.error_type || 'Error'}</span>
                  <span className="text-gray-500 text-xs">{err.created_at ? new Date(err.created_at).toLocaleString(isAr ? 'ar-SA' : 'en-US') : ''}</span>
                </div>
                <p className="text-red-600 font-mono text-xs mt-1 break-all">{err.error_message}</p>
                {err.page && <p className="text-gray-500 text-xs mt-1">{isAr ? 'الصفحة:' : 'Page:'} {err.page}</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ServerHealth;
